/**
 * Agent Ran Event Component
 *
 * Renders ahoy_agent_ran run records — one agent autonomy evaluation, written
 * on EVERY run while the agent is active (not only approvals). The header
 * leads with what happened per disposition (approved / partial / held). The
 * body box carries the proposer's reasoning (`summary`, markdown) when
 * present; otherwise a generic line per disposition fills the box so it is
 * never empty.
 *
 * Per-action detail (approved counts, held-reason sentences) is deliberately
 * NOT rendered here (owner decision 2026-09-01): the pending proposals
 * themselves are where held work surfaces, and the auto-approval banner on a
 * proposal explains an approval. The reason codes stay machine-readable in
 * the payload.
 *
 * proposal_id / resolved_proposal_id / lane_proposal_id /
 * approved_action_ids / held[] are raw internal values and are deliberately
 * never rendered. Payload shape may be malformed (historical rows carrying
 * the retired `level` key, backend drift), so every field is read
 * defensively, unknown keys are ignored, and this renderer must never throw.
 */
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';

import { useValidOrgId } from '../../hooks/useValidOrgId';
import {
  ActorName,
  InlineText,
  MARKDOWN_ALERT_CLASSNAME,
  TimelineEvent,
} from '../components/shared';
import type { AgentRanPayload } from '../types';
import { useEventFormatters } from '../useEventFormatters';
import { parseNonEmptyString } from '../utils/payloadValidation';
import type { EventComponentProps } from './types';

type Disposition = 'approved' | 'partial' | 'held';

/** A disposition we can render — 'unknown' covers malformed/legacy rows. */
type DisplayDisposition = Disposition | 'unknown';

const HEADER_COPY: Record<DisplayDisposition, string> = {
  approved: 'approved automatically',
  partial: 'approved part of an action',
  held: 'held an action for review',
  // Never claim an approval we can't verify: a malformed or legacy row gets
  // a neutral header, not the fail-open 'approved automatically'.
  unknown: 'ran',
};

const GENERIC_BODY_COPY: Record<DisplayDisposition, string> = {
  approved: 'This run needed no manual review.',
  partial: 'Part of this run needed no manual review.',
  held: 'This run is waiting for manual review.',
  unknown: "We couldn't load the details of this run.",
};

function normalizeDisposition(value: unknown): DisplayDisposition {
  return value === 'approved' || value === 'partial' || value === 'held'
    ? value
    : 'unknown';
}

export function AgentRanEvent({ event }: EventComponentProps) {
  const { getActorInfo } = useEventFormatters();
  const actor = getActorInfo(event);

  const validOrgId = useValidOrgId();

  const payload = event.payload as AgentRanPayload | undefined;
  const disposition = normalizeDisposition(payload?.disposition);
  const summary = parseNonEmptyString(payload?.summary);

  const headerCopy = HEADER_COPY[disposition];
  const ariaLabel = `${actor.name} ${headerCopy}`;

  const hasSummary = summary !== undefined && validOrgId !== undefined;
  const showGenericFallback = !hasSummary;

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={
          <InlineText>
            <ActorName actor={actor} /> {headerCopy}
          </InlineText>
        }
        timestamp={event.occurred_at}
      >
        <div className='mt-2 rounded-md border px-3 py-2 border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] space-y-2'>
          {hasSummary && (
            <MarkdownContent
              content={summary}
              orgId={validOrgId}
              size='compact'
              className={MARKDOWN_ALERT_CLASSNAME}
            />
          )}
          {showGenericFallback && <p>{GENERIC_BODY_COPY[disposition]}</p>}
        </div>
      </TimelineEvent.Content>
    </TimelineEvent>
  );
}
